import {
	AMPLITUDE_API_KEY,
	AMPLITUDE_EXPERIMENT_KEY,
	NODE_ENV,
} from '@/constants/api'
import { useEffect } from 'react'
import * as amplitude from '@amplitude/analytics-browser'
import { Experiment, ExperimentUser } from '@amplitude/experiment-js-client'

interface AmplitudeSetupParams {
	userId?: number | null
	company?: string | null
}

export enum ExperimentType {
	BANNER_COMPANY_FIRST_ACCESS = 'corporate-banner-company-first-access',
}

type ExperimentInstance = ReturnType<
	typeof Experiment.initializeWithAmplitudeAnalytics
>
let experimentInstance: ExperimentInstance | null = null

export enum EventType {
	SKILL_MAPPING_VIEWED = 'skill_mapping_viewed',
}

export enum MadebyType {
	STUDENT = 'student',
}

interface AmplitudeEventParams {
	event: EventType
	optionalParams?: { trending?: string; remaining_seats?: number }
}

function getPrefixedUserId(userId?: number | null): string | null {
	return userId != null ? `corporate_${userId}` : null
}

export function sendAmplitudeEvent({
	event,
	optionalParams,
}: AmplitudeEventParams) {
	const params = {
		made_by: MadebyType.STUDENT,
		...optionalParams,
	}

	if (NODE_ENV !== 'production') {
		// eslint-disable-next-line no-console
		console.log('Amplitude event', { event, params })
		return
	}

	amplitude.track(event, params)
}

export const AmplitudeScript = ({ userId, company }: AmplitudeSetupParams) => {
	useEffect(() => {
		if (NODE_ENV !== 'production') return

		if (!AMPLITUDE_API_KEY) {
			console.warn('Amplitude API key is missing')
			return
		}

		if (!userId) {
			console.warn('Amplitude userId is missing')
			return
		}

		const prefixedUserId = getPrefixedUserId(userId)

		amplitude
			.init(AMPLITUDE_API_KEY, prefixedUserId!, {
				autocapture: {
					attribution: true,
					pageViews: true,
					sessions: true,
					formInteractions: false,
					fileDownloads: false,
					elementInteractions: false,
				},
				...(NODE_ENV &&
					['production'].includes(NODE_ENV) && {
						serverUrl: 'https://amplitude2.fluencyacademy.io/2/httpapi',
					}),
			})
			.promise.then(async () => {
				// IDENTIFY user with custom properties
				if (company) {
					const identifyObj = new amplitude.Identify()
					identifyObj.set('company', company)
					amplitude.identify(identifyObj)
				}

				// 👇 Evento explícito que garante envio de device_id, platform, etc.
				amplitude.track('session_initialized')

				// Initialize experiment and fetch variant
				experimentInstance = Experiment.initializeWithAmplitudeAnalytics(
					AMPLITUDE_EXPERIMENT_KEY!
				)

				const user: ExperimentUser = {
					user_id: prefixedUserId!,
					device_id: amplitude.getDeviceId(),
				}

				await experimentInstance.fetch(user)
			})
	}, [userId, company])

	return null
}

export function fetchExperiment(flagKey: ExperimentType): boolean {
	const FLAG_OFF_VALUE = 'not-allowed'
	const FLAG_ON_VALUE = 'allowed'

	if (!experimentInstance) {
		experimentInstance = Experiment.initializeWithAmplitudeAnalytics(
			AMPLITUDE_EXPERIMENT_KEY!
		)
	}

	const variant = experimentInstance.variant(flagKey, FLAG_OFF_VALUE)
	const experimentResult = variant.value || FLAG_OFF_VALUE

	return experimentResult === FLAG_ON_VALUE
}
